"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { pusherClient } from "@/lib/utils/pusher";
import { NotificationToast } from "./NotificationToast";

interface PusherError {
	type: string;
	error: string;
	data?: Record<string, unknown>;
}

interface ChatNotificationData {
	message: string;
	chatId: string;
}

export function NotificationManager() {
	const { data: session } = useSession();
	const router = useRouter();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isAudioReady, setIsAudioReady] = useState(false);

	useEffect(() => {
		// Create audio element
		const audio = new Audio();
		audio.src = "/sounds/notification.mp3";
		audio.volume = 0.5; // Set volume to 50%

		// Handle audio loading
		const handleCanPlay = () => {
			setIsAudioReady(true);
		};

		const handleError = (error: Event) => {
			console.error("Error loading notification sound:", error);
			setIsAudioReady(false);
		};

		audio.addEventListener("canplaythrough", handleCanPlay);
		audio.addEventListener("error", handleError);

		// Load the audio
		audio.load();
		audioRef.current = audio;

		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.removeEventListener("canplaythrough", handleCanPlay);
				audioRef.current.removeEventListener("error", handleError);
				audioRef.current = null;
			}
		};
	}, []);

	const playNotificationSound = () => {
		if (audioRef.current && isAudioReady) {
			audioRef.current.currentTime = 0; // Reset the audio to start
			const playPromise = audioRef.current.play();

			if (playPromise !== undefined) {
				playPromise.catch((error) => {
					console.error("Error playing notification sound:", error);
				});
			}
		}
	};

	useEffect(() => {
		if (!session?.user) return;

		// Subscribe to user's personal channel
		const userChannel = pusherClient.subscribe(`user-${session.user.id}`);

		// Subscribe to admin notifications if user is admin
		if (session?.user?.role === "ADMIN") {
			console.log(
				"[Notifications] Attempting to subscribe to admin notifications"
			);
			const adminChannel = pusherClient.subscribe("admin-notifications");
			console.log(
				"[Notifications] Admin channel subscription object:",
				adminChannel
			);

			adminChannel.bind("new-chat", (data: ChatNotificationData) => {
				console.log(
					"[Notifications] Received new-chat event on admin channel:",
					data
				);
				playNotificationSound();
				toast.custom(
					(t) => (
						<NotificationToast
							message={data.message}
							type="admin"
							onClick={() => {
								toast.dismiss(t);
								router.push(`/admin/chat/${data.chatId}`);
							}}
						/>
					),
					{ duration: 5000 }
				);
			});

			adminChannel.bind("pusher:subscription_succeeded", () => {
				console.log(
					"[Notifications] Successfully subscribed to admin notifications channel"
				);
			});

			adminChannel.bind("pusher:subscription_error", (error: PusherError) => {
				console.error(
					"[Notifications] Error subscribing to admin notifications:",
					error
				);
			});

			// Debug: Log all events on the admin channel
			adminChannel.bind_global(
				(eventName: string, data: ChatNotificationData) => {
					console.log(
						`[Notifications] Admin channel event: ${eventName}`,
						data
					);
				}
			);

			return () => {
				console.log(
					"[Notifications] Cleaning up admin notifications subscription"
				);
				adminChannel.unbind_all();
				adminChannel.unsubscribe();
			};
		}

		// Handle new chat messages for regular users
		if ((session.user.role as "USER" | "ADMIN") !== "ADMIN") {
			userChannel.bind(
				"new-message",
				(data: { message: string; chatId: string }) => {
					playNotificationSound();
					toast.custom(
						(t) => (
							<NotificationToast
								message={data.message}
								type="chat"
								onClick={() => {
									toast.dismiss(t);
									router.push(`/dashboard/chat/${data.chatId}`);
								}}
							/>
						),
						{ duration: 5000 }
					);
				}
			);
		}

		return () => {
			console.log("[NotificationManager] Cleaning up subscriptions");
			userChannel.unbind_all();
			userChannel.unsubscribe();
		};
	}, [session, router]);

	return null;
}
